import React, { useState, CSSProperties, useCallback, useMemo } from 'react';
import styles from './icon-tooltip.module.scss';
import InfoPopup from '../../atoms/info-popup/info-popup';
import { getHeapAttributes } from '../../../utils/heap/heap.helper';

export type Props = {
  svgIcon: JSX.Element;
  svgIconColor: string;
  activeSVGIconColor: string;
  tooltipAccessibilityLabel: string;
  heading?: string;
  helperClass?: string;
  borderRadius?: string;
  heapIdentifier?: any;
  heapDataContent?: string;
  children: React.ReactNode;
};

export default function IconTooltip({
  svgIcon,
  svgIconColor,
  activeSVGIconColor,
  tooltipAccessibilityLabel,
  heading,
  helperClass,
  borderRadius = '50%',
  heapIdentifier,
  heapDataContent,
  children,
}: Props) {
  const [popupContent, setPopupContent] = useState<React.ReactNode>();

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setPopupContent(children);
    },
    [children],
  );

  const renderData = useMemo(() => {
    const heapAttributes =
      heapIdentifier && getHeapAttributes(heapIdentifier, { tooltip: heapDataContent });

    const buttonStyle = {
      borderRadius: borderRadius,
      '--icon-fill': svgIconColor,
      '--icon-fill-active': activeSVGIconColor,
    } as CSSProperties;

    return { heapAttributes, buttonStyle };
  }, [activeSVGIconColor, borderRadius, heapDataContent, heapIdentifier, svgIconColor]);

  return (
    <div className={`${styles['icon-tooltip-block']} ${helperClass || ''}`}>
      <button
        className={styles['icon-tooltip-action']}
        style={renderData.buttonStyle}
        {...renderData.heapAttributes}
        onClick={onClick}
        aria-haspopup='dialog'
      >
        {tooltipAccessibilityLabel}
        <span>{svgIcon}</span>
      </button>
      {!!popupContent && (
        <InfoPopup
          heading={heading}
          onClose={() => setPopupContent(null)}
          cssClass={styles['icon-tooltip-content']}
        >
          {popupContent}
        </InfoPopup>
      )}
    </div>
  );
}
